import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { claimGrants } from "../lib/api.js";
import {
  confirmForgotPassword,
  confirmSignUp,
  forgotPassword,
  getGroups,
  getUserEmail,
  getUserSub,
  isCognitoConfigured,
  isGoogleEnabled,
  isSignedIn,
  beginGoogleSignIn,
  completeOAuthCode,
  refreshSession,
  getRefreshToken,
  resendConfirmationCode,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  signUp as cognitoSignUp,
} from "../lib/auth.js";

export interface AuthUser {
  sub: string;
  email: string;
  groups: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  googleEnabled: boolean;
  configured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    captchaToken?: string
  ) => Promise<{ userConfirmed: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  startGoogleSignIn: () => Promise<void>;
  finishOAuthCallback: (code: string) => Promise<void>;
  signOut: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readUser(): AuthUser | null {
  if (!isSignedIn()) return null;
  return {
    sub: getUserSub(),
    email: getUserEmail(),
    groups: getGroups(),
  };
}

async function afterSignIn(): Promise<AuthUser | null> {
  await claimGrants();
  return readUser();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!isSignedIn() && isCognitoConfigured() && getRefreshToken()) {
      await refreshSession().catch(() => false);
    }
    setUser(readUser());
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await refreshUser();
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshUser]);

  const signIn = useCallback(async (email: string, password: string) => {
    await cognitoSignIn(email, password);
    setUser(await afterSignIn());
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, captchaToken?: string) => {
      return cognitoSignUp(email, password, captchaToken);
    },
    []
  );

  const confirm = useCallback(async (email: string, code: string) => {
    await confirmSignUp(email, code);
  }, []);

  const resendCode = useCallback(async (email: string) => {
    await resendConfirmationCode(email);
  }, []);

  const forgot = useCallback(async (email: string) => {
    await forgotPassword(email);
  }, []);

  const confirmForgot = useCallback(async (email: string, code: string, newPassword: string) => {
    await confirmForgotPassword(email, code, newPassword);
  }, []);

  const startGoogleSignIn = useCallback(async () => {
    const url = await beginGoogleSignIn();
    window.location.assign(url);
  }, []);

  const finishOAuthCallback = useCallback(async (code: string) => {
    await completeOAuthCode(code);
    setUser(await afterSignIn());
  }, []);

  const signOut = useCallback(() => {
    cognitoSignOut();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      googleEnabled: isGoogleEnabled(),
      configured: isCognitoConfigured(),
      signIn,
      signUp,
      confirmSignUp: confirm,
      resendCode,
      forgotPassword: forgot,
      confirmForgotPassword: confirmForgot,
      startGoogleSignIn,
      finishOAuthCallback,
      signOut,
      refreshUser,
    }),
    [
      user,
      loading,
      signIn,
      signUp,
      confirm,
      resendCode,
      forgot,
      confirmForgot,
      startGoogleSignIn,
      finishOAuthCallback,
      signOut,
      refreshUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
